import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn, PaletteUtils } from '@shared/utils';

interface IButtonProps {
  // BASE
  className?: string;
  color?: 'primary' | 'outlined' | 'icon' | 'dashed';
  fullWidth?: boolean;
  loading?: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  children: ReactNode;
  // LINK
  to?: string;
  // BUTTON
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  disabled?: boolean;
}

export const Button = (props: IButtonProps) => {
  const { t } = useTranslation();

  const className = cn(
    // BASE
    'relative flex appearance-none items-center justify-center gap-3 whitespace-nowrap transition-all duration-200',
    PaletteUtils.isInsigniaPalette && 'rounded-sm',
    PaletteUtils.isVcfcPalette && 'rounded-3xl',
    // PRIMARY
    props.color === 'primary' && [
      // BASE PRIMARY
      'h-10 px-4 py-2',
      // INSIGNIA
      PaletteUtils.isInsigniaPalette && [
        'border-transparent bg-soft-black text-primary hover:bg-white-signal-black hover:shadow-lg active:bg-transport-black active:shadow-md dark:bg-primary dark:text-soft-black dark:hover:bg-pale-taupe dark:active:bg-gray-beige',
        props.disabled && 'opacity-50',
      ],
      // VCFC
      PaletteUtils.isVcfcPalette && [
        'border-transparent bg-primary text-ice',
        'hover:bg-midnight-graphite',
        props.disabled && 'bg-light text-dark-wet-asphalt',
      ],
    ],
    // OUTLINED
    props.color === 'outlined' && [
      // BASE OUTLINED
      'h-10 border px-4 py-2',
      // INSIGNIA
      PaletteUtils.isInsigniaPalette && [
        'border-soft-black bg-ice text-soft-black hover:bg-gainsborough hover:shadow-lg active:bg-light-gray active:shadow-md dark:border-primary dark:bg-eerie-black dark:text-primary dark:hover:bg-signal-dark-black dark:hover:shadow-lg dark:active:bg-white-signal-black dark:active:shadow-md',
        props.disabled && 'opacity-50',
      ],
      PaletteUtils.isVcfcPalette && [
        'border-primary bg-white text-primary dark:border-white dark:bg-transparent dark:text-white',
        'hover:bg-gainsborough dark:hover:bg-dark-wet-asphalt',
        'active:bg-light dark:active:bg-signal-dark-black',
        props.disabled && 'opacity-40',
      ],
    ],
    // DASHED
    props.color === 'dashed' && [
      // BASE DASHED
      'h-10 border border-dashed px-4 py-2',
      // INSIGNIA
      PaletteUtils.isInsigniaPalette && [
        'border-almost-gray text-slate dark:text-white',
        'hover:bg-smoky-white hover:text-white-signal-black hover:shadow-lg dark:hover:bg-signal-dark-black dark:hover:text-light-telegray',
        'active:bg-gray-gainsborough active:text-almost-black active:shadow-md dark:active:bg-white-signal-black dark:active:text-pearlescent-light-gray',
        props.disabled && 'opacity-50',
      ],
      // VCFC
      PaletteUtils.isVcfcPalette && [
        'border-primary bg-white text-primary dark:border-white dark:bg-transparent dark:text-white',
        'hover:bg-gainsborough dark:hover:bg-dark-wet-asphalt',
        'active:bg-light dark:active:bg-signal-dark-black',
        props.disabled && 'border-dark-wet-asphalt opacity-50',
      ],
    ],
    // ICON
    props.color === 'icon' && [
      'inline-flex h-fit items-center justify-center p-2 text-center transition-all duration-200 ease-in-out',
      'hover:bg-black/5 dark:hover:bg-white/10',
      PaletteUtils.isVcfcPalette && 'rounded-sm',
      props.disabled && 'opacity-50',
    ],
    // DEFAULT
    !props.color && [
      'h-10 px-4 py-2 text-soft-black hover:text-white-signal-black active:text-almost-black dark:text-white dark:hover:text-light-telegray dark:active:text-pearlescent-light-gray',
      props.disabled && 'opacity-50',
    ],
    props.disabled && 'pointer-events-none',
    props.fullWidth && 'w-full',
    props.className,
  );

  if (props.to) {
    return (
      <Link className={className} to={props.to} onClick={props.onClick}>
        {props.children}
      </Link>
    );
  }
  return (
    <button className={className} type={props.type || 'button'} form={props.form} disabled={props.disabled || props.loading} onClick={props.onClick}>
      {props.loading ? t('buttons.loading') : props.children}
    </button>
  );
};
